/**
 * Top navigation bar component
 *
 * Desktop-only horizontal navigation bar with navigation links.
 * Shows nav items in the header, replacing the sidebar.
 * Hidden on mobile (md and below), visible on desktop (lg+).
 *
 * V1-inspired: pill-shaped container for nav group,
 * clean active states, teal accent on primary action with glow.
 */
import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  TOP_NAV_ITEMS,
  groupNavItemsForTopNav,
  isActive,
  isHighlightedItem,
  getTopNavLinkClasses,
  getTopNavContainerClasses,
  getTopNavPrimaryContainerClasses,
} from "./navigation-utils";

/**
 * Desktop top navigation bar
 *
 * Displays navigation links horizontally:
 * - Main items (Dashboard, Timeline, Events, Trends) in a pill-shaped container
 * - Primary items (AI Check-in) with prominent button styling + glow
 */
export function TopNavBar() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const { main, primary } = groupNavItemsForTopNav(TOP_NAV_ITEMS);

  return (
    <>
      {/* Main navigation links - pill container */}
      <nav className={getTopNavContainerClasses()} aria-label={t("nav.mainNavigation")}>
        {main.map((item) => {
          const active = isActive(location.pathname, item.path);
          const highlighted = isHighlightedItem(item);
          const Icon = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              hash=""
              className={cn(getTopNavLinkClasses(active, highlighted))}
              aria-current={active ? "page" : undefined}
            >
              <Icon className={cn("h-4 w-4", active && "text-primary")} aria-hidden="true" />
              <span>{t(item.labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      {/* Primary action (AI Check-in) - separated for visual prominence */}
      {primary.length > 0 && (
        <div className={getTopNavPrimaryContainerClasses()}>
          {primary.map((item) => {
            const active = isActive(location.pathname, item.path);
            const highlighted = isHighlightedItem(item);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                hash=""
                className={cn(getTopNavLinkClasses(active, highlighted))}
                aria-current={active ? "page" : undefined}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
